import React, { useState, useEffect } from 'react';
import { Search, Edit, Trash2, FileText, Calendar, DollarSign, Clock, Shield, Package, AlertTriangle, Eye, Filter, ArrowUpDown, ArrowDown, ArrowUp, Download, ExternalLink, Target, ChevronLeft, ChevronRight } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { Tender } from '../types';
import TenderForm from './TenderForm';
import TenderDetailNavigator from './TenderDetailNavigator';
import { useTenant } from '../contexts/TenantContext';
import ApplicationForm from './ApplicationForm';
import TendersOnboardingModal from './TendersOnboardingModal';

const TendersList: React.FC = () => {
  const { 
    tenders, 
    organizations, 
    businessLines,
    tenderTypes, 
    tenderStatuses, 
    applicationStatuses,
    applications,
    deleteTender,
    updateTender
  } = useData(); 
  const { currentCompany, currentCompanyUser } = useTenant();
  
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [editingTender, setEditingTender] = useState<Tender | null>(null);
  const [viewingTender, setViewingTender] = useState<Tender | null>(null);
  const [selectedTenderForApplication, setSelectedTenderForApplication] = useState<Tender | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [flujoFilter, setFlujoFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState<string>('closing_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showTendersOnboarding, setShowTendersOnboarding] = useState(false);

  // Verificar si debe mostrar el onboarding de licitaciones
  useEffect(() => {
    console.log('Current company user:', currentCompanyUser);
    if (currentCompanyUser && !currentCompanyUser.Onboarding2) {
      console.log('Mostrando onboarding de licitaciones');
      setShowTendersOnboarding(true);
    }
  }, [currentCompanyUser]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  // Get current date at midnight for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Filter tenders by current company
  const companyTenders = tenders.filter(tender => tender.company_id === currentCompany?.id);

  const filteredTendersAll = companyTenders
    .filter(tender => {
      // Only show tenders where closing date is greater than or equal to today
      const closingDate = new Date(tender.closing_date);
      closingDate.setHours(0, 0, 0, 0);
      const isNotClosed = closingDate >= today;

      // Exclude tenders that have adjudicated applications
      const adjudicatedStatus = applicationStatuses.find(status => status.name === 'Adjudicada');
      const hasAdjudicatedApplication = applications.some(app => 
        app.tender_id === tender.id && 
        adjudicatedStatus && app.status_id === adjudicatedStatus.id
      );

      const matchesSearch = tender.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tender.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tender.tender_code.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFlujo = !flujoFilter || tender.flujo === flujoFilter;
      return matchesSearch && matchesFlujo && isNotClosed && !hasAdjudicatedApplication;
    })
    .sort((a, b) => {
      // Primero ordenar por flujo: Disponible, En Revision, Aceptada, Descartada
      const flujoOrder = {
        'Disponible': 1,
        'En Revision': 2,
        'Aceptada': 3,
        'Descartada': 4,
        'Adjudicada': 5
      };
      
      const flujoA = a.flujo || 'Disponible';
      const flujoB = b.flujo || 'Disponible';
      const flujoComparison = (flujoOrder[flujoA as keyof typeof flujoOrder] || 99) - 
                             (flujoOrder[flujoB as keyof typeof flujoOrder] || 99);
      
      // Si los flujos son diferentes, usar el orden de flujo
      if (flujoComparison !== 0) {
        return flujoComparison;
      }
      
      // Si los flujos son iguales, ordenar por días hasta vencimiento (menos días primero)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const closingDateA = new Date(a.closing_date);
      closingDateA.setHours(0, 0, 0, 0);
      const daysUntilClosingA = Math.ceil((closingDateA.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      const closingDateB = new Date(b.closing_date);
      closingDateB.setHours(0, 0, 0, 0);
      const daysUntilClosingB = Math.ceil((closingDateB.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      return daysUntilClosingA - daysUntilClosingB;
    });

  // Calculate pagination
  const totalTenders = filteredTendersAll.length;
  const totalPages = Math.ceil(totalTenders / pageSize);
  
  // Get current page of tenders
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const filteredTenders = filteredTendersAll.slice(startIndex, endIndex);
  
  // Handle page changes
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };
  const handleEdit = (tender: Tender) => {
    // Check if tender has applications
    const hasBids = applications.some(app => app.tender_id === tender.id);
    
    if (!hasBids) {
      setEditingTender(tender);
      setShowForm(true);
    }
  };

  const handleView = (tender: Tender) => {
    setViewingTender(tender);
    setShowDetail(true);
  };

  const handleDelete = (id: string) => {
    // Verificar si la licitación tiene postulaciones
    const hasApplications = applications.some(app => app.tender_id === id);
    
    if (hasApplications) {
      return;
    }
    
    if (window.confirm('¿Estás seguro de que deseas eliminar esta licitación?')) {
      deleteTender(id);
    }
  };

  const handleCreateApplication = (tender: Tender) => {
    // Verificar si la licitación está en estado "Publicada"
    const tenderStatus = tenderStatuses.find(s => s.id === tender.status_id);
    const isPublished = tenderStatus?.name === 'Publicada';
    
    // Verificar si ya existe una postulación para esta licitación
    const hasExistingApplication = hasApplication(tender.id);
    
    if (!isPublished) {
      alert('Solo se pueden crear postulaciones para licitaciones en estado "Publicada".');
      return;
    }
    
    if (hasExistingApplication) {
      alert('Ya existe una postulación para esta licitación.');
      return;
    }
    
    // Actualizar el flujo a "Aceptada"
    updateTender(tender.id, { flujo: 'Aceptada' });
    
    setSelectedTenderForApplication(tender);
    setShowApplicationForm(true);
  };

  const handleUpdateFlujo = (tender: Tender, newFlujo: 'Disponible' | 'En Revision' | 'Aceptada' | 'Descartada' | 'Adjudicada') => {
    // No permitir cambiar a "Adjudicada" manualmente (esto se hace automáticamente)
    if (newFlujo === 'Adjudicada') {
      alert('El estado "Adjudicada" se actualiza automáticamente cuando una postulación es adjudicada.');
      return;
    }
    
    // No permitir cambiar a "Aceptada" si ya tiene una postulación
    if (newFlujo === 'Aceptada' && hasApplication(tender.id)) {
      alert('Esta licitación ya tiene una postulación asociada.');
      return;
    }
    
    // Confirmar cambio a "Descartada"
    if (newFlujo === 'Descartada' && !confirm(`¿Estás seguro de que deseas marcar esta licitación como "Descartada"?`)) {
      return;
    }
    
    updateTender(tender.id, { flujo: newFlujo });
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTender(null);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setViewingTender(null);
  };

  const handleCloseApplicationForm = () => {
    setShowApplicationForm(false);
    setSelectedTenderForApplication(null);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${currency} ${amount.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getDaysRemaining = (closingDate: string) => {
    const today = new Date();
    const closing = new Date(closingDate);
    
    // Reset time to start of day for accurate day calculation
    today.setHours(0, 0, 0, 0);
    closing.setHours(0, 0, 0, 0);
    
    const diffTime = closing.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  const getDaysRemainingDisplay = (closingDate: string) => {
    const daysRemaining = getDaysRemaining(closingDate);
    
    if (daysRemaining === 0) {
      return {
        text: 'Cerrada',
        color: 'text-red-600 bg-red-100',
        icon: AlertTriangle
      };
    } else if (daysRemaining <= 3) {
      return {
        text: `${daysRemaining} día${daysRemaining !== 1 ? 's' : ''}`,
        color: 'text-red-600 bg-red-100',
        icon: AlertTriangle
      };
    } else if (daysRemaining <= 7) {
      return {
        text: `${daysRemaining} días`,
        color: 'text-orange-600 bg-orange-100',
        icon: Clock
      };
    } else {
      return {
        text: `${daysRemaining} días`,
        color: 'text-green-600 bg-green-100',
        icon: Clock
      };
    }
  };

  const getOrganizationName = (id: string) => {
    return organizations.find(org => org.id === id)?.name || 'No especificada';
  };

  const getTenderTypeName = (id: string) => {
    return tenderTypes.find(type => type.id === id)?.name || 'No especificado';
  };
  
  const getFlujoColor = (flujo: string = 'Disponible') => {
    switch (flujo) {
      case 'Disponible':
        return 'bg-blue-100 text-blue-800';
              case 'En Revision':
          return 'bg-purple-100 text-purple-800';
      case 'Aceptada':
        return 'bg-green-100 text-green-800';
      case 'Descartada':
        return 'bg-red-100 text-red-800';
      case 'Adjudicada':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Verificar si una licitación ya tiene una postulación
  const hasApplication = (tenderId: string) => {
    return applications.some(app => app.tender_id === tenderId);
  };

  // Verificar si una licitación está en estado "Publicada"
  const isPublishedTender = (tenderId: string) => {
    const tender = tenders.find(t => t.id === tenderId);
    if (!tender) return false;
    
    const status = tenderStatuses.find(s => s.id === tender.status_id);
    return status?.name === 'Publicada';
  };

  if (showForm) {
    return (
      <TenderForm
        tender={editingTender}
        onClose={handleCloseForm}
      />
    );
  }

  if (showDetail) {
    return (
      <TenderDetailNavigator
        tenders={filteredTendersAll}
        initialIndex={filteredTendersAll.findIndex(t => t.id === viewingTender?.id)}
        onClose={handleCloseDetail}
        onCreateApplication={handleCreateApplication}
      />
    );
  }

  if (showApplicationForm && selectedTenderForApplication) {
    return (
      <ApplicationForm
        application={null}
        onClose={handleCloseApplicationForm}
        preselectedTenderId={selectedTenderForApplication.id}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Licitaciones</h1>
          <p className="text-gray-600">Gestiona las licitaciones de {currentCompany?.name}</p>
        </div>
        <div className="flex space-x-3">
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              <FileText className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              <Filter className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4">
          {/* Flow Summary */}
          <div className="grid grid-cols-5 gap-2">
            {['Disponible', 'En Revision', 'Aceptada', 'Descartada', 'Adjudicada'].map(flow => {
              let count;
              if (flow === 'Adjudicada') {
                // Para adjudicadas, contar aplicaciones adjudicadas en lugar de licitaciones
                const adjudicatedStatus = applicationStatuses.find(status => status.name === 'Adjudicada');
                count = applications.filter(app => 
                  app.company_id === currentCompany?.id && 
                  adjudicatedStatus && app.status_id === adjudicatedStatus.id
                ).length;
              } else {
                // Para otros flujos, contar licitaciones normalmente
                count = tenders.filter(t => t.company_id === currentCompany?.id && t.flujo === flow).length;
              }
              const colorClass = 
                flow === 'Disponible' ? 'bg-blue-100 text-blue-800' :
                flow === 'En Revision' ? 'bg-purple-100 text-purple-800' :
                flow === 'Aceptada' ? 'bg-green-100 text-green-800' :
                flow === 'Descartada' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800';
              
              return (
                <div 
                  key={flow}
                  onClick={() => setFlujoFilter(flujoFilter === flow ? '' : flow)}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg cursor-pointer border ${
                    flujoFilter === flow 
                      ? 'border-2 border-gray-800 shadow-md' 
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <div className={`text-lg font-bold ${colorClass.split(' ')[1]}`}>{count}</div>
                  <div className={`text-xs font-medium px-2 py-1 rounded-full mt-1 ${colorClass}`}>
                    {flow}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar licitaciones por título, descripción o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
                {searchTerm && filteredTendersAll.length === 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3 mt-1 z-10">
                    <p className="text-sm text-gray-600">No se encontraron licitaciones con el término "{searchTerm}"</p>
                  </div>
                )}
            </div>
            <div className="sm:w-48">
              <select
                value={flujoFilter}
                onChange={(e) => setFlujoFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos los flujos</option>
                <option value="Disponible">Disponible</option>
                <option value="En Revision">En Revision</option>
                <option value="Aceptada">Aceptada</option>
                <option value="Descartada">Descartada</option>
                <option value="Adjudicada">Adjudicada</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Acciones
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('tender_code')}
                  >
                    <div className="flex items-center">
                      <span>Código</span>
                      <span className="ml-1">{getSortIcon('tender_code')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center">
                      <span>Título</span>
                      <span className="ml-1">{getSortIcon('title')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('organization')}
                  >
                    <div className="flex items-center">
                      <span>Organización</span>
                      <span className="ml-1">{getSortIcon('organization')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center">
                      <span>Monto</span>
                      <span className="ml-1">{getSortIcon('amount')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('closing_date')}
                  >
                    <div className="flex items-center">
                      <span>Cierre</span>
                      <span className="ml-1">{getSortIcon('closing_date')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('flujo')}
                  >
                    <div className="flex items-center">
                      <span>Flujo</span>
                      <span className="ml-1">{getSortIcon('flujo')}</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTenders.length > 0 ? (
                  filteredTenders.map((tender) => {
                    const daysDisplay = getDaysRemainingDisplay(tender.closing_date);
                    const IconComponent = daysDisplay.icon;
                    const hasExistingApplication = hasApplication(tender.id);
                    const isPublished = isPublishedTender(tender.id);
                    
                    return (
                      <tr key={tender.id} className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleView(tender)}
                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Ver detalles"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(tender)}
                              disabled={hasExistingApplication}
                              className={`p-1 rounded transition-colors ${
                                hasExistingApplication
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                              }`}
                              title={hasExistingApplication ? "No se puede modificar una licitación con postulaciones" : "Editar licitación"}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdateFlujo(tender, 'Descartada')}
                              className={`p-1 rounded transition-colors ${
                                hasExistingApplication
                                  ? 'text-gray-300 cursor-not-allowed' 
                                  : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              }`}
                              title={
                                hasExistingApplication 
                                  ? "No se puede eliminar una licitación con postulaciones" 
                                  : "Descartar licitación"
                              }
                              disabled={hasExistingApplication}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleCreateApplication(tender)}
                              className={`p-1 rounded transition-colors ${
                                !isPublished || hasExistingApplication 
                                  ? 'text-gray-300 cursor-not-allowed' 
                                  : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                              }`}
                              title={
                                !isPublished 
                                  ? "Solo se pueden crear postulaciones para licitaciones en estado 'Publicada'" 
                                  : hasExistingApplication 
                                    ? "Ya existe una postulación para esta licitación" 
                                    : "Crear postulación para esta licitación"
                              }
                              disabled={!isPublished || hasExistingApplication}
                            >
                              <Target className="h-4 w-4" />
                            </button>
                            <a 
                              href={`https://www.mercadopublico.cl/fichaLicitacion.html?idLicitacion=${tender.tender_code}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                              title="Ver en Mercado Público"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {tender.tender_code}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-medium text-gray-900 line-clamp-2">
                            {tender.title}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {getTenderTypeName(tender.type_id)}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {getOrganizationName(tender.organization_id)}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-green-600">
                            {formatCurrency(tender.amount, tender.currency)}
                          </div>
                          {tender.contract_months && (
                            <div className="text-xs text-gray-500">
                              {tender.contract_months} meses
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(tender.closing_date)}
                          </div>
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${daysDisplay.color} mt-1`}>
                            <IconComponent className="h-3 w-3 mr-1" />
                            {daysDisplay.text}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getFlujoColor(tender.flujo)}`}>
                              {tender.flujo || 'Disponible'}
                            </div>
                            <div className="relative ml-2 group">
                              <button className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                                <ArrowDown className="h-3 w-3" />
                              </button>
                              <div className="absolute right-0 mt-1 w-36 bg-white shadow-lg rounded-lg py-1 z-10 hidden group-hover:block">
                                <button 
                                  onClick={() => handleUpdateFlujo(tender, 'Disponible')}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Disponible
                                </button>
                                <button 
                                  onClick={() => handleUpdateFlujo(tender, 'En Revision')}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Leída
                                </button>
                                <button 
                                  onClick={() => handleUpdateFlujo(tender, 'Aceptada')}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Aceptada
                                </button>
                                <button 
                                  onClick={() => handleUpdateFlujo(tender, 'Descartada')}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Descartada
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center">
                      <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No hay licitaciones</h3>
                      <p className="text-gray-600 mb-6">
                        {searchTerm || flujoFilter ? 'No se encontraron licitaciones con los filtros aplicados.' : 'Comienza agregando tu primera licitación.'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalTenders > 0 && (
            <div className="px-3 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between items-center">
              <div>
                Mostrando {startIndex + 1}-{Math.min(endIndex, totalTenders)} de {totalTenders} licitaciones
              </div>
              <div className="flex items-center space-x-2">
                <select 
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                  className="px-2 py-1 text-xs border border-gray-300 rounded"
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="250">250</option>
                </select>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    className={`p-1 rounded ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <ChevronLeft className="h-4 w-4 -ml-2" />
                  </button>
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-1 rounded ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-2">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-1 rounded ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`p-1 rounded ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                    <ChevronRight className="h-4 w-4 -ml-2" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTenders.length > 0 ? (
            filteredTenders.map((tender) => {
              const daysDisplay = getDaysRemainingDisplay(tender.closing_date);
              const IconComponent = daysDisplay.icon;
              const hasExistingApplication = hasApplication(tender.id);
              const isPublished = isPublishedTender(tender.id);
              
              return (
                <div key={tender.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {tender.tender_code}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${daysDisplay.color}`}>
                            <IconComponent className="h-3 w-3 mr-1" />
                            {daysDisplay.text}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 mb-2">
                          {tender.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-3">
                          {tender.description}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleView(tender)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver detalles"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(tender)}
                          disabled={hasExistingApplication}
                          className={`p-2 rounded-lg transition-colors ${
                            hasExistingApplication
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                          }`}
                          title={hasExistingApplication ? "No se puede modificar una licitación con postulaciones" : "Editar licitación"}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tender.id)}
                          disabled={hasExistingApplication || tender.flujo === 'Aceptada'}
                          className={`p-2 rounded-lg transition-colors ${
                            hasExistingApplication || tender.flujo === 'Aceptada'
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                          title={
                            hasExistingApplication 
                              ? "No se puede eliminar una licitación con postulaciones" 
                              : tender.flujo === 'Aceptada'
                                ? "No se pueden eliminar licitaciones en estado 'Aceptada'" 
                                : "Eliminar licitación"
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <FileText className="h-4 w-4 mr-2" />
                        <span>{getOrganizationName(tender.organization_id)}</span>
                        {tender.tender_line_id && (
                          <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            {businessLines.find(bl => bl.id === tender.tender_line_id as string)?.name || 'Línea de negocio'}
                          </span>
                        )}
                        {tender.tender_line_id && (
                          <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            {businessLines.find(bl => bl.id === tender.tender_line_id as string)?.name || 'Línea de negocio'}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                        <span className="font-medium text-green-600">
                          {formatCurrency(tender.amount, tender.currency)}
                        </span>
                        {tender.contract_months && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({tender.contract_months} meses)
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>Cierre: {formatDate(tender.closing_date)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {tender.has_technical_visit && (
                            <div className="flex items-center text-orange-600">
                              <Clock className="h-3 w-3 mr-1" />
                              <span className="text-xs">Visita Técnica</span>
                            </div>
                          )}
                          {tender.fecha_preguntas && (
                            <div className="flex items-center text-purple-600">
                              <Clock className="h-3 w-3 mr-1" />
                              <span className="text-xs">Preguntas</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Items information */}
                      {tender.items_count !== undefined && tender.items_count > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Package className="h-4 w-4 mr-2 text-orange-600" />
                          <span className="font-medium text-orange-600">
                            {tender.items_count} unidades solicitadas
                          </span>
                          {tender.items && tender.items.length > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              ({tender.items.length} tipo{tender.items.length !== 1 ? 's' : ''} de item{tender.items.length !== 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                      )}

                      {/* Fecha límite para preguntas */}
                      {tender.fecha_preguntas && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="h-4 w-4 mr-2 text-purple-600" />
                          <span>Preguntas hasta: {formatDate(tender.fecha_preguntas)}</span>
                        </div>
                      )}

                      {/* Garantías */}
                      {(tender.has_gso || tender.has_gfc) && (
                        <div className="flex items-center space-x-3 text-xs">
                          {tender.has_gso && (
                            <div className="flex items-center text-purple-600">
                              <Shield className="h-3 w-3 mr-1" />
                              <span>GSO: {formatCurrency(tender.gso_amount || 0, tender.currency)}</span>
                            </div>
                          )}
                          {tender.has_gfc && (
                            <div className="flex items-center text-indigo-600">
                              <Shield className="h-3 w-3 mr-1" />
                              <span>GFC: {formatCurrency(tender.gfc_amount || 0, tender.currency)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-gray-500">
                            {getTenderTypeName(tender.type_id)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-right">
                            <span 
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFlujoColor(tender.flujo)}`}
                            >
                              {tender.flujo || 'Disponible'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleUpdateFlujo(tender, 'Descartada')}
                            disabled={hasExistingApplication}
                            className={`p-1 rounded transition-colors ${
                              hasExistingApplication
                                ? 'text-gray-300 cursor-not-allowed' 
                                : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                            }`}
                            title={
                              !isPublished 
                                ? "Solo se pueden crear postulaciones para licitaciones en estado 'Publicada'" 
                                : "Descartar licitación"
                            }
                          >
                            <Target className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full min-h-[300px]">
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay licitaciones</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || flujoFilter ? 'No se encontraron licitaciones con los filtros aplicados.' : 'Comienza agregando tu primera licitación.'}
                </p>
              </div>
            </div>
          )}
          
          {/* Pagination for grid view */}
          {totalTenders > 0 && (
            <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Mostrando {startIndex + 1}-{Math.min(endIndex, totalTenders)} de {totalTenders} licitaciones
              </div>
              <div className="flex items-center space-x-2">
                <select 
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="250">250</option>
                </select>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    className={`p-1 rounded ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <ChevronLeft className="h-4 w-4 -ml-2" />
                  </button>
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-1 rounded ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-2">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-1 rounded ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`p-1 rounded ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                    <ChevronRight className="h-4 w-4 -ml-2" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      {filteredTenders.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas <span className="text-sm font-normal text-gray-500">({totalTenders} licitaciones encontradas)</span></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              className="flex items-center justify-center px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
            >
              <Download className="h-5 w-5 mr-2" />
              <span>Exportar a Excel</span>
            </button>
            <button
              className="flex items-center justify-center px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <Search className="h-5 w-5 mr-2" />
              <span>Buscar en Mercado Público</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Modal de onboarding de licitaciones */}
      <TendersOnboardingModal
        isOpen={showTendersOnboarding}
        onClose={() => setShowTendersOnboarding(false)}
        onComplete={() => setShowTendersOnboarding(false)}
      />
    </div>
  );
};

export default TendersList;